/**
 * PORTAL CLIENTE v2 — PLANNER DE SÓLO LECTURA (PC-5)
 *
 * El cliente ve CUÁNDO se limpia y cómo va cada limpieza, con las mismas vistas que el Planner interno (Día, Semana,
 * Mes) — reutilizadas, no copiadas — pero en modo `soloLectura`: nada se arrastra ni se estira, no hay selección
 * múltiple, ni «Nueva OT», ni copiar semana, ni panel de gestión. La autoridad no es esta pantalla: los datos salen
 * de `GET /api/portal/planner` (sin operarios, sin alertas internas, sin acciones) y todas las rutas de mutación
 * del Planner siguen exigiendo permisos que CLIENTE no tiene (403, probado en el backend).
 *
 * Clic en una OT → su ficha del portal (/mis-ots/:id), con trazabilidad por tarea.
 *
 * PC-v2.2-B: el resumen del periodo enseña sólo lo que la configuración del centro elegido deja
 * (`GET /api/portal/indicadores`). Las cinco situaciones son UN indicador: o todas o ninguna. El calendario y las
 * fichas no cambian.
 *
 * PC-v2.2-C2.3-B: «Registro de limpiezas y verificaciones» (E3) en Excel, con el periodo de la vista (ampliable hasta
 * 92 días sin tocar el calendario) y el centro elegido o todos.
 */
import { type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api, type EventoPlannerPortal, type MetricasDia, type PlannerEvento, type PlannerEventoMaterializado, type PlannerPortal } from '../api.ts';
import { Cargando, EmptyState, Error as ErrorCaja, LeyendaEstados, useDatos, useEsMovil, Vacio } from '../componentes/base.tsx';
import { GraficoSituacionesOt, indicadoresOt, situacionesOt } from '../componentes/cifras-ot.tsx';
import { IND, useIndicadoresPortal } from '../componentes/indicadores-portal.ts';
import { ExportarRegistroLimpiezas } from '../componentes/exportar-registro-limpiezas.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useListaUrl } from '../listaUrl.ts';
import {
  diasDelRango, etiquetaDiaLarga, hoyIso, LEYENDA_LIMPIEZA, ListaCronologica, rangoDeVista, sumarDias, VistaDiaHoraria, VistaMes,
  type Vista,
} from './planner.tsx';
import { useCentrosPortal } from './portal-inicio.tsx';

type VistaPortal = Exclude<Vista, 'timeline'>;
const VISTAS: readonly { clave: VistaPortal; texto: string }[] = [
  { clave: 'dia', texto: 'Día' },
  { clave: 'semana', texto: 'Semana' },
  { clave: 'mes', texto: 'Mes' },
];

const DEFAULTS = { vista: 'semana', fecha: '', idCentro: '' } as const;

/**
 * El evento del portal con la forma que esperan las vistas del Planner. Lo que el portal no trae (operarios,
 * alertas, plan) va vacío y TODAS las acciones a falso: aunque una vista olvidara `soloLectura`, nada sería movible.
 */
export function eventoPlannerDePortal(e: EventoPlannerPortal): PlannerEventoMaterializado {
  return {
    tipo: 'OT',
    idOt: e.idOt,
    codigo: e.codigo,
    estado: e.estado,
    origen: '',
    fecha: e.fecha,
    inicioLocal: e.inicioLocal,
    inicioProgramadoDesconocido: e.inicioProgramadoDesconocido,
    duracionPlanificadaMin: e.duracionMin,
    duracionBaseMin: e.duracionMin,
    idPlan: null,
    codigoPlan: null,
    idSala: '',
    codigoSala: e.codigoSala,
    descripcionSala: e.descripcionSala,
    tareas: e.tareas,
    operariosAsignados: 0,
    operarios: [],
    alertas: [],
    acciones: { puedeReprogramar: false, puedeRedimensionar: false, puedeAsignar: false, puedeDesasignar: false, motivosBloqueo: [] },
    avance: e.avance,
  };
}

/** Mes: sólo cuántas OT hay cada día — nunca carga ni capacidad. */
function metricasDeMes(eventos: readonly EventoPlannerPortal[]): MetricasDia[] {
  const porDia = new Map<string, number>();
  for (const e of eventos) porDia.set(e.fecha, (porDia.get(e.fecha) ?? 0) + 1);
  return [...porDia.entries()].map(([fecha, numeroOts]) => ({
    fecha, numeroOts, numeroOtsActivas: numeroOts, minutosCarga: 0, minutosCapacidad: 0, operariosSinConfigurar: 0,
    otsSinAsignar: 0, operariosDistintos: 0, otsConAlertas: 0, numeroAlertas: 0,
  }));
}

function desplazar(vista: VistaPortal, fecha: string, sentido: 1 | -1): string {
  if (vista === 'dia') return sumarDias(fecha, sentido);
  if (vista === 'semana') return sumarDias(fecha, 7 * sentido);
  const [a, m] = fecha.split('-').map(Number);
  return new Date(Date.UTC(a ?? 2026, (m ?? 1) - 1 + sentido, 1)).toISOString().slice(0, 10);
}

/** «Lunes, 26 de octubre de 2026 – domingo, 1 de noviembre de 2026»: mayúscula sólo al principio. */
function textoRango(vista: VistaPortal, desde: string, hasta: string): string {
  const t = vista === 'dia' ? etiquetaDiaLarga(desde)
    : vista === 'mes' ? new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${desde}T00:00:00Z`))
      : `${etiquetaDiaLarga(desde)} – ${etiquetaDiaLarga(hasta)}`;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function PaginaPortalPlanner(): ReactNode {
  const lista = useListaUrl(DEFAULTS);
  const vistaUrl = lista.valor('vista');
  const vista: VistaPortal = vistaUrl === 'dia' || vistaUrl === 'mes' ? vistaUrl : 'semana';
  const fecha = lista.valor('fecha') || hoyIso();
  const centros = useCentrosPortal();
  //  Con un solo centro no hay nada que elegir; con varios, el primero hasta que el cliente cambie.
  const idCentro = lista.valor('idCentro') || (centros[0]?.id ?? '');
  const { desde, hasta } = rangoDeVista(vista, fecha);
  const esMovil = useEsMovil();
  const navegar = useNavigate();
  const ubicacion = useLocation();

  const consulta = new URLSearchParams({ idCentro, desde, hasta }).toString();
  const datos = useDatos(
    () => (idCentro === '' ? Promise.resolve(null) : api.get<PlannerPortal>(`/api/portal/planner?${consulta}`)),
    [consulta],
  );
  //  Nunca se pinta la ventana anterior mientras llega la nueva.
  const p = datos.datos !== null && datos.datos.idCentro === idCentro && datos.datos.desde === desde && datos.datos.hasta === hasta
    ? datos.datos : null;
  const eventos: PlannerEvento[] = p === null ? [] : p.eventos.map(eventoPlannerDePortal);
  //  La configuración de ESTE centro; mientras llega, no hay resumen (nunca el del centro anterior).
  const indicadores = useIndicadoresPortal(idCentro);
  const abrir = (ev: PlannerEvento): void => {
    if (ev.tipo !== 'OT') return;
    void navegar(`/mis-ots/${ev.idOt}`, { state: { volver: { a: `${ubicacion.pathname}${ubicacion.search}`, texto: 'Volver al calendario' } } });
  };
  const irADia = (f: string): void => lista.fijarVarios({ vista: 'dia', fecha: f });

  return (
    <Shell titulo="Calendario de limpieza" subtitulo="Cuándo se limpia y cómo va cada limpieza. Sólo consulta.">
      <div className="panel">
        <div className="panel-cuerpo portal-filtros">
          {centros.length > 1 && (
            <div className="campo">
              <label htmlFor="portal-planner-centro">Centro</label>
              <select id="portal-planner-centro" value={idCentro} onChange={(e) => lista.fijar('idCentro', e.target.value)}>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <div className="campo">
            <span className="etiqueta-campo" id="portal-planner-vista">Vista</span>
            <div className="fila" role="group" aria-labelledby="portal-planner-vista" style={{ gap: 6 }}>
              {VISTAS.map((v) => (
                <button key={v.clave} type="button" className={vista === v.clave ? 'principal' : 'secundario'} aria-pressed={vista === v.clave}
                  onClick={() => lista.fijar('vista', v.clave)}>
                  {v.texto}
                </button>
              ))}
            </div>
          </div>
          <div className="campo">
            <span className="etiqueta-campo">Periodo</span>
            <div className="fila" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="secundario" aria-label="Periodo anterior" onClick={() => lista.fijar('fecha', desplazar(vista, fecha, -1))}>‹</button>
              <button type="button" className="secundario" onClick={() => lista.fijar('fecha', '')}>Hoy</button>
              <button type="button" className="secundario" aria-label="Periodo siguiente" onClick={() => lista.fijar('fecha', desplazar(vista, fecha, 1))}>›</button>
              <strong className="portal-planner-rango">{textoRango(vista, desde, hasta)}</strong>
            </div>
          </div>
        </div>
      </div>

      {idCentro !== '' && (
        <ExportarRegistroLimpiezas perspectiva="PORTAL" idCentro={idCentro} varios={centros.length > 1} desdeVista={desde} hastaVista={hasta} />
      )}
      {p !== null && indicadores.visibles !== null && <PanelPortal panel={p.panel} v={indicadores.visibles} />}
      {idCentro !== '' && indicadores.error !== null && <ErrorCaja error={indicadores.error} reintentar={indicadores.recargar} />}

      {centros.length === 0 && !datos.cargando && <Vacio titulo="No hay centros con servicio contratado." />}
      {idCentro !== '' && datos.cargando && p === null && <Cargando texto="Cargando calendario…" />}
      {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
      {p !== null && (
        <>
          {eventos.length === 0 && <EmptyState titulo="No hay limpiezas programadas en este periodo." descripcion="Cambia de periodo o de centro." />}
          {eventos.length > 0 && vista !== 'mes' && <LeyendaEstados titulo="Estados" estados={LEYENDA_LIMPIEZA} />}
          {vista === 'dia' && eventos.length > 0 && (
            esMovil
              ? <ListaCronologica eventos={eventos} abrir={abrir} />
              : <VistaDiaHoraria dias={[desde]} eventos={eventos} abrir={abrir} idCentro={idCentro} zonaHoraria={p.zonaHoraria}
                  desde={desde} hasta={hasta} alGuardado={() => undefined} soloLectura />
          )}
          {vista === 'semana' && eventos.length > 0 && (
            esMovil
              ? <ListaCronologica eventos={eventos} abrir={abrir} agruparPorDia />
              : <VistaDiaHoraria dias={diasDelRango(desde, hasta)} eventos={eventos} abrir={abrir} idCentro={idCentro}
                  zonaHoraria={p.zonaHoraria} desde={desde} hasta={hasta} alGuardado={() => undefined} verDia={irADia} soloLectura />
          )}
          {vista === 'mes' && <VistaMes fecha={fecha} dias={metricasDeMes(p.eventos)} eventos={eventos} irADia={irADia} soloLectura />}
        </>
      )}
    </Shell>
  );
}

/**
 * PC-v2.1-A — dos bloques que no se mezclan: «Órdenes de trabajo» (cada limpieza en una sola situación, que suman el
 * total; «Con incidencia» y «Fuera de plazo» aparte, porque se solapan) y «Tareas». Mismas palabras que el Planner
 * interno (`componentes/cifras-ot.tsx`); las incidencias que cuentan son sólo las que el cliente ve.
 */
function PanelPortal({ panel, v }: { panel: PlannerPortal['panel']; v: ReadonlySet<string> }): ReactNode {
  const o = panel.ots;
  const t = panel.tareas;
  const total = v.has(IND.calendario.total);
  const situaciones = v.has(IND.calendario.situaciones);
  //  PC-v2.2-C1 — G5: las mismas cinco situaciones en una barra (el servidor ya lo exige ligado a `situaciones`).
  const grafico = situaciones && v.has(IND.calendario.situacionesGrafico);
  const transversales = indicadoresOt(o).filter((c) =>
    v.has(c.clave === 'con-incidencia' ? IND.calendario.conIncidencia : IND.calendario.fueraDePlazo));
  const conOts = total || situaciones || transversales.length > 0;
  const conTareas = v.has(IND.calendario.tareas);
  if (!conOts && !conTareas) return null;
  const tareas: { clave: string; texto: string; valor: string }[] = [
    { clave: 'hechas', texto: 'Tareas hechas', valor: t.total === 0 ? '—' : `${String(t.realizadas)} de ${String(t.total)}` },
    { clave: 'verificadas', texto: 'Verificadas', valor: String(t.verificadas) },
    { clave: 'no-aplica', texto: 'No aplicaban', valor: String(t.noAplica) },
    { clave: 'pendientes', texto: 'Pendientes', valor: String(t.pendientes) },
  ];
  return (
    <section className="portal-planner-resumen" aria-label="Resumen del periodo">
      {conOts && (
      <div className="portal-planner-grupo" role="group" aria-labelledby="portal-panel-ots">
        <h2 id="portal-panel-ots" className="portal-planner-grupo-titulo">Órdenes de trabajo</h2>
        {(total || situaciones) && (
        <div className="portal-planner-panel">
          {total && (
          <div className="portal-planner-cifra portal-planner-cifra-total">
            <span className="portal-kpi-cifra">{o.total}</span>
            <span className="pequena suave">Limpiezas</span>
          </div>
          )}
          {situaciones && situacionesOt(o).map((c) => (
            <div key={c.clave} className="portal-planner-cifra" data-cifra={c.clave}>
              <span className="portal-kpi-cifra">{c.valor}</span>
              <span className="pequena suave">{c.texto}</span>
            </div>
          ))}
        </div>
        )}
        {grafico && (
        <div className="portal-planner-grafico">
          <GraficoSituacionesOt o={o} />
        </div>
        )}
        {transversales.length > 0 && (
        <div className="portal-planner-panel portal-planner-transversales" aria-label="Indicadores que se solapan con las situaciones">
          {transversales.map((c) => (
            <div key={c.clave} className="portal-planner-cifra" data-cifra={c.clave}>
              <span className="portal-kpi-cifra">{c.valor}</span>
              <span className="pequena suave">{c.texto}</span>
            </div>
          ))}
        </div>
        )}
      </div>
      )}
      {conTareas && (
      <div className="portal-planner-grupo" role="group" aria-labelledby="portal-panel-tareas">
        <h2 id="portal-panel-tareas" className="portal-planner-grupo-titulo">Tareas</h2>
        <div className="portal-planner-panel">
          {tareas.map((c) => (
            <div key={c.clave} className="portal-planner-cifra" data-cifra={`tareas-${c.clave}`}>
              <span className="portal-kpi-cifra">{c.valor}</span>
              <span className="pequena suave">{c.texto}</span>
            </div>
          ))}
        </div>
      </div>
      )}
    </section>
  );
}

